/**
 * Invoice Inbox — mailbox settings.
 *
 * Where an address is connected to the register. Until this screen existed the
 * only way in was a row written into the database by hand, so a company could
 * not start collecting invoices without a developer.
 *
 * Deliberately not the Desk mailbox form:
 * - the mailbox belongs to the COMPANY, so everyone with the settings
 *   permission sees the same list and the same failures;
 * - IMAP only — nothing here ever sends mail;
 * - health answers "is it working?" with what actually goes wrong: mail that
 *   arrived and became nothing, and a mailbox that has gone quiet. A green
 *   "last synced" chip proves neither, because a successful sweep clears the
 *   error field.
 *
 * Native form controls on purpose, like desk-settings: the catalyst controls
 * are Tailwind-4-styled and render broken under this project's Tailwind 3.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Mail, RefreshCw } from "lucide-react";
import { Heading, Subheading } from "catalyst-ui-kit/heading";
import { Text } from "catalyst-ui-kit/text";
import { Badge } from "catalyst-ui-kit/badge";
import {
  deleteInvoiceMailbox,
  fetchInvoiceMailboxes,
  refreshInvoiceMailboxes,
  saveInvoiceMailbox,
  testInvoiceMailbox,
} from "services/invoice-inbox.service";
import { InvoiceMailbox, InvoiceMailboxPayload } from "types/InvoiceInbox";
import { formatPrettyDate } from "utils/helperFunctions";

const inputCls =
  "w-full rounded-lg border border-zinc-300 bg-transparent px-3 py-1.5 text-base md:text-sm outline-none transition-all duration-150 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:border-zinc-700 dark:focus:ring-indigo-400/20 dark:focus:border-indigo-400";
const btnCls =
  "rounded-lg border border-zinc-200/80 bg-transparent px-3 py-1.5 text-sm font-medium hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900 transition-colors duration-150 disabled:opacity-40 max-md:min-h-11";
const primaryBtnCls =
  "rounded-lg bg-indigo-600 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 active:bg-indigo-700 shadow-sm transition-colors duration-150 disabled:opacity-40 max-md:min-h-11 dark:bg-indigo-500 dark:hover:bg-indigo-400";
const dangerBtnCls =
  "rounded-lg bg-red-600 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-red-500 active:bg-red-700 shadow-sm transition-colors duration-150 disabled:opacity-40 max-md:min-h-11 dark:bg-red-500 dark:hover:bg-red-400";

/**
 * Guessing `imap.<domain>` sends a vardas@yandex.lt account to imap.yandex.lt,
 * which is not where that mail lives — so the two providers we know get fixed
 * hosts and only "other" keeps the guess.
 */
const PROVIDER_PRESETS: Record<string, { imapHost: string; imapPort: string }> = {
  yandex: { imapHost: "imap.yandex.com", imapPort: "993" },
  gmail: { imapHost: "imap.gmail.com", imapPort: "993" },
};

const EMPTY_FORM = {
  address: "",
  provider: "other",
  imapHost: "",
  imapPort: "993",
  folder: "",
  appPassword: "",
  ingestMode: "all",
  labelName: "",
  isActive: true,
};

type FormState = typeof EMPTY_FORM;

const isPortValid = (value: string) => {
  const port = Number(value);
  return Boolean(value.trim()) && Number.isInteger(port) && port >= 1 && port <= 65535;
};

export default function InvoiceInboxSettings() {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();

  const [mailboxes, setMailboxes] = useState<InvoiceMailbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  const [hasCredentials, setHasCredentials] = useState(false);
  const [hostsTouched, setHostsTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<"disable" | "delete" | null>(null);
  // Filled by the connection test: the folder to sweep cannot be guessed
  // (providers localise the names, a shared or collected mailbox hangs
  // wherever that provider put it), so the test reads the real list back.
  const [knownFolders, setKnownFolders] = useState<string[]>([]);

  const applyForm = useCallback((mailbox: InvoiceMailbox) => {
    setSelectedId(mailbox.id);
    setIsAdding(false);
    setHasCredentials(mailbox.hasCredentials);
    setHostsTouched(true);
    setFieldError(null);
    setPendingRemoval(null);
    // The folder list belongs to the account that was tested. Carrying it to
    // another mailbox would offer paths that do not exist there — and a picked
    // one saves clean and then collects nothing (bugbot #692).
    setKnownFolders([]);
    setForm({
      address: mailbox.address,
      provider: mailbox.provider,
      imapHost: mailbox.imapHost,
      imapPort: String(mailbox.imapPort),
      folder: mailbox.folder ?? "",
      appPassword: "",
      ingestMode: mailbox.ingestMode,
      labelName: mailbox.labelName || "",
      isActive: mailbox.isActive,
    });
  }, []);

  const load = useCallback(
    async (preferId?: string | null) => {
      if (!companyId) return;
      try {
        const res = await fetchInvoiceMailboxes(companyId);
        setLoadError(null);
        const rows = res.mailboxes || [];
        setMailboxes(rows);
        // The form must always describe a row that is still in the list.
        // Falling through to "leave it as it was" left the deleted mailbox on
        // screen after deleting one of several — and Save then submitted its id.
        const target =
          (preferId && rows.find((m) => m.id === preferId)) || rows[0] || null;
        if (target) {
          applyForm(target);
        } else {
          // Nothing connected yet: the empty form IS the screen.
          setSelectedId(null);
          setIsAdding(true);
          setKnownFolders([]);
          setHasCredentials(false);
          setHostsTouched(false);
          setForm({ ...EMPTY_FORM });
        }
      } catch (e: any) {
        // A failed load must not read as "no mailboxes" — that is how a second
        // copy of an address that is already collecting gets created. And a 403
        // is not a failure at all: the register and its settings are separate
        // rights, so somebody can reach this screen without holding the second.
        setLoadError(
          e?.response?.status === 403
            ? t("invoice_mailbox_forbidden", {
                defaultValue: "You do not have the right to manage the invoice mailboxes.",
              })
            : e?.response?.data?.message ||
              t("invoice_mailbox_load_failed", { defaultValue: "Could not load the mailboxes." })
        );
      } finally {
        setLoading(false);
      }
    },
    [companyId, applyForm, t]
  );

  useEffect(() => {
    load();
  }, [load]);

  const set = (key: keyof FormState, value: string | boolean) =>
    setForm((current) => ({ ...current, [key]: value }));

  const onProviderChange = (provider: string) => {
    const preset = PROVIDER_PRESETS[provider];
    setForm((current) => ({
      ...current,
      provider,
      // Never silently overwrite a host somebody typed on purpose.
      imapHost: preset && !hostsTouched ? preset.imapHost : current.imapHost,
      imapPort: preset && !hostsTouched ? preset.imapPort : current.imapPort,
      ingestMode: provider === "gmail" ? current.ingestMode : "all",
    }));
  };

  const onAddressChange = (address: string) => {
    setForm((current) => {
      const preset = PROVIDER_PRESETS[current.provider];
      const domain = address.split("@")[1] || "";
      return {
        ...current,
        address,
        imapHost: hostsTouched ? current.imapHost : preset ? preset.imapHost : domain ? "imap." + domain : "",
      };
    });
  };

  const editingId = isAdding ? null : selectedId;
  const selected = useMemo(
    () => (editingId ? mailboxes.find((m) => m.id === editingId) || null : null),
    [editingId, mailboxes]
  );
  const addressChanged = Boolean(selected && selected.address !== form.address.trim().toLowerCase());
  const stillConnected = Boolean(selected && (selected.isActive || hasCredentials));

  const payload = (): InvoiceMailboxPayload => ({
    ...(editingId ? { id: editingId } : {}),
    address: form.address.trim().toLowerCase(),
    provider: form.provider,
    imapHost: form.imapHost || undefined,
    imapPort: Number(form.imapPort),
    folder: form.folder,
    appPassword: form.appPassword || undefined,
    ingestMode: form.ingestMode,
    labelName: form.labelName || undefined,
    isActive: form.isActive,
  });

  const startAdding = () => {
    setIsAdding(true);
    setKnownFolders([]);
    setSelectedId(null);
    setHasCredentials(false);
    setHostsTouched(false);
    setFieldError(null);
    setPendingRemoval(null);
    setForm({ ...EMPTY_FORM });
  };

  /** Field-bound problems stay next to the field; outcomes go to a toast. */
  const saveErrorText = (e: any): string | null => {
    const data = e?.response?.data || {};
    if (data.code === "ADDRESS_TAKEN") {
      return t("invoice_mailbox_address_taken", {
        defaultValue: "This address is already collected into the register.",
      });
    }
    if (data.code === "PASSWORD_REQUIRED") {
      return t("invoice_mailbox_password_required", { defaultValue: "An app password is required." });
    }
    if (data.code === "PASSWORD_REQUIRED_ADDRESS_CHANGE") {
      return t("invoice_mailbox_password_required_address_change", {
        defaultValue: "The address changed — enter the app password of the new account.",
      });
    }
    if (data.code === "LABEL_MODE_GMAIL_ONLY") {
      return t("invoice_mailbox_label_gmail_only", { defaultValue: "Label mode works with Gmail only." });
    }
    if (data.code === "HOST_NOT_ALLOWED") {
      return t("invoice_mailbox_host_not_allowed", { defaultValue: "This mail host is not allowed." });
    }
    if (data.code === "INVALID_ADDRESS") {
      return t("invoice_mailbox_invalid_address", { defaultValue: "Enter a valid email address." });
    }
    if (data.code === "INVALID_FOLDER") {
      return t("invoice_mailbox_invalid_folder", { defaultValue: "Invalid folder name." });
    }
    return null;
  };

  const test = async () => {
    if (!companyId) return;
    setBusy(true);
    try {
      const res = await testInvoiceMailbox(companyId, payload());
      setKnownFolders(res.folders || []);
      if (!res.ok) {
        toast.error(t("invoice_mailbox_test_failed", { defaultValue: "Could not sign in to the mailbox." }));
      } else if (form.ingestMode === "label" && res.labelFound === false) {
        // The silent one: a label that does not exist makes every sweep skip
        // every message while the mailbox still reports itself healthy.
        toast.error(
          t("invoice_mailbox_label_missing", {
            defaultValue: "Signed in, but there is no such label — nothing would be collected.",
          })
        );
      } else if (res.folderFound === false) {
        // Same trap, one field over: a folder that does not exist under a
        // different name skips every sweep while the mailbox looks healthy.
        toast.error(
          t("invoice_mailbox_folder_missing", {
            defaultValue: "Signed in, but there is no such folder — nothing would be collected.",
          })
        );
      } else {
        toast.success(t("invoice_mailbox_test_ok", { defaultValue: "The mailbox answered." }));
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || t("error"));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!companyId) return;
    setFieldError(null);
    if (!isPortValid(form.imapPort)) {
      setFieldError(t("invoice_mailbox_invalid_port", { defaultValue: "Enter a port between 1 and 65535." }));
      return;
    }
    if (addressChanged && !form.appPassword) {
      setFieldError(
        t("invoice_mailbox_password_required_address_change", {
          defaultValue: "The address changed — enter the app password of the new account.",
        })
      );
      return;
    }
    setBusy(true);
    try {
      const res = await saveInvoiceMailbox(companyId, payload());
      setForm((current) => ({ ...current, appPassword: "" }));
      setHasCredentials(true);
      setIsAdding(false);
      const newId = res?.mailboxId || editingId;
      if (newId) setSelectedId(newId);
      await load(newId);
      toast.success(t("saved", { defaultValue: "Saved" }));
    } catch (e: any) {
      const inline = saveErrorText(e);
      if (inline) setFieldError(inline);
      else toast.error(e?.response?.data?.message || e?.message || t("error"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (mode: "disable" | "delete") => {
    if (!companyId || !editingId) return;
    setBusy(true);
    try {
      await deleteInvoiceMailbox(companyId, editingId, mode);
      setPendingRemoval(null);
      toast.success(
        mode === "delete"
          ? t("invoice_mailbox_deleted", { defaultValue: "The mailbox was deleted." })
          : t("invoice_mailbox_disabled", { defaultValue: "Collection stopped." })
      );
      await load(mode === "delete" ? null : editingId);
    } catch (e: any) {
      const data = e?.response?.data || {};
      toast.error(
        data.code === "MAILBOX_HAS_MESSAGES"
          ? t("invoice_mailbox_has_messages", {
              defaultValue: "This mailbox has already received mail — stop collection instead.",
              count: data.messageCount || 0,
            })
          : data.code === "MAILBOX_STILL_CONNECTED"
            ? t("invoice_mailbox_disconnect_first", {
                defaultValue: "Stop collection before deleting the mailbox.",
              })
            : data.message || e?.message || t("error")
      );
    } finally {
      setBusy(false);
    }
  };

  const checkMailNow = async () => {
    if (!companyId || !editingId) return;
    setBusy(true);
    try {
      const res = await refreshInvoiceMailboxes(companyId, editingId);
      const result = (res.mailboxes || [])[0];
      if (!result) return;
      if (result.status === "swept") {
        toast.success(
          result.processed
            ? t("invoice_mailbox_swept_new", {
                defaultValue: "Checked — {{count}} new message(s).",
                count: result.processed,
              })
            : t("invoice_mailbox_swept_none", { defaultValue: "Checked — no new mail." })
        );
      } else if (result.status === "busy") {
        toast.success(t("invoice_mailbox_busy", { defaultValue: "A check is already running." }));
      } else {
        // offline / disconnected / timeout / unavailable all mean the same to
        // the person in front of the screen: it was NOT checked.
        toast.error(
          t("invoice_mailbox_not_checked", {
            defaultValue: "The mailbox was not checked ({{status}}).",
            status: result.status,
          })
        );
      }
      await load(editingId);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || t("error"));
    } finally {
      setBusy(false);
    }
  };

  const showForm = !loadError && (isAdding || !!selectedId);

  return (
    <div className="mx-auto max-w-full p-6">
      <Link
        to={`/app/${companyId}/invoice-inbox`}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-300"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("invoice_inbox", { defaultValue: "Incoming invoices" })}
      </Link>

      <Heading>{t("invoice_mailboxes", { defaultValue: "Invoice mailboxes" })}</Heading>
      <Text className="mt-1 !text-sm">
        {t("invoice_mailboxes_intro", {
          defaultValue:
            "Mail arriving at these addresses becomes documents in the register. The address belongs to the company, not to you — everyone who manages the settings sees it.",
        })}
      </Text>

      {loadError && (
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {loadError}
          <button type="button" className={btnCls + " ml-3"} onClick={() => load()}>
            {t("retry", { defaultValue: "Retry" })}
          </button>
        </div>
      )}

      {loading ? (
        <div className="mt-8 text-sm text-zinc-500">{t("loading", { defaultValue: "Loading…" })}</div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
          {/* The list */}
          <div className="space-y-3">
            {mailboxes.map((mailbox) => (
              <button
                key={mailbox.id}
                type="button"
                onClick={() => applyForm(mailbox)}
                className={
                  "w-full rounded-xl border p-3 text-left transition-colors duration-150 " +
                  (mailbox.id === selectedId && !isAdding
                    ? "border-indigo-400 bg-indigo-50/60 dark:border-indigo-500 dark:bg-indigo-950/30"
                    : "border-zinc-200 hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900")
                }
              >
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Mail className="h-4 w-4 shrink-0 text-zinc-400" />
                  <span className="truncate">{mailbox.address}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {mailbox.isActive && mailbox.hasCredentials ? (
                    <Badge color="green">{t("invoice_mailbox_collecting", { defaultValue: "Collecting" })}</Badge>
                  ) : (
                    <Badge color="zinc">{t("invoice_mailbox_off", { defaultValue: "Not collecting" })}</Badge>
                  )}
                  {mailbox.lastError && (
                    <Badge color="red">{t("invoice_mailbox_error", { defaultValue: "Error" })}</Badge>
                  )}
                  {mailbox.ingestErrorCount > 0 && (
                    <Badge color="red">
                      {t("invoice_mailbox_lost", {
                        defaultValue: "{{count}} message(s) lost",
                        count: mailbox.ingestErrorCount,
                      })}
                    </Badge>
                  )}
                  {mailbox.silent && (
                    <Badge color="amber">
                      {t("invoice_mailbox_silent", {
                        defaultValue: "Quiet for {{count}} day(s)",
                        count: mailbox.quietDays,
                      })}
                    </Badge>
                  )}
                </div>
              </button>
            ))}

            <button type="button" className={btnCls + " w-full"} onClick={startAdding}>
              + {t("invoice_mailbox_add", { defaultValue: "Add a mailbox" })}
            </button>
          </div>

          {/* The form */}
          {showForm && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-zinc-200/80 p-5 dark:border-zinc-800">
                <Subheading className="!mb-4 !font-semibold">
                  {isAdding
                    ? t("invoice_mailbox_add", { defaultValue: "Add a mailbox" })
                    : t("invoice_mailbox_edit", { defaultValue: "Mailbox settings" })}
                </Subheading>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                      {t("email", { defaultValue: "Email address" })}
                    </span>
                    <input
                      className={inputCls}
                      type="email"
                      value={form.address}
                      placeholder="saskaitos@imone.lt"
                      onChange={(e) => onAddressChange(e.target.value)}
                    />
                  </label>

                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                      {t("invoice_mailbox_provider", { defaultValue: "Provider" })}
                    </span>
                    <select
                      className={inputCls}
                      value={form.provider}
                      onChange={(e) => onProviderChange(e.target.value)}
                    >
                      <option value="other">{t("invoice_mailbox_provider_other", { defaultValue: "Other (IMAP)" })}</option>
                      <option value="gmail">Gmail</option>
                      <option value="yandex">Yandex</option>
                    </select>
                  </label>

                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                      {t("invoice_mailbox_imap_host", { defaultValue: "IMAP server" })}
                    </span>
                    <input
                      className={inputCls}
                      value={form.imapHost}
                      onChange={(e) => {
                        setHostsTouched(true);
                        set("imapHost", e.target.value);
                      }}
                    />
                  </label>

                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                      {t("invoice_mailbox_imap_port", { defaultValue: "IMAP port" })}
                    </span>
                    <input
                      className={inputCls}
                      value={form.imapPort}
                      onChange={(e) => {
                        setHostsTouched(true);
                        set("imapPort", e.target.value);
                      }}
                    />
                  </label>

                  <label className="block md:col-span-2">
                    <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                      {t("invoice_mailbox_folder", { defaultValue: "Folder" })}
                    </span>
                    <input
                      className={inputCls}
                      value={form.folder}
                      placeholder="INBOX"
                      list="invoice-mailbox-folders"
                      onChange={(e) => set("folder", e.target.value)}
                    />
                    {knownFolders.length > 0 && (
                      <datalist id="invoice-mailbox-folders">
                        {knownFolders.map((path) => (
                          <option key={path} value={path} />
                        ))}
                      </datalist>
                    )}
                    <span className="mt-1 block text-xs text-zinc-500">
                      {t("invoice_mailbox_folder_hint", {
                        defaultValue:
                          "Leave empty to collect the whole inbox (INBOX). A mail-server rule can file invoices into a separate folder — only mail that arrives after the change is collected.",
                      })}
                    </span>
                    {knownFolders.length > 0 && (
                      <span className="mt-1 block text-xs text-zinc-500">
                        {t("invoice_mailbox_folders_found", {
                          count: knownFolders.length,
                          defaultValue: "The connection test found {{count}} folders — pick one from the list.",
                        })}
                      </span>
                    )}
                  </label>

                  <label className="block md:col-span-2">
                    <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                      {t("invoice_mailbox_app_password", { defaultValue: "App password" })}
                    </span>
                    <input
                      className={inputCls}
                      type="password"
                      autoComplete="new-password"
                      value={form.appPassword}
                      placeholder={
                        hasCredentials
                          ? t("invoice_mailbox_password_stored", { defaultValue: "Saved — leave empty to keep it" })
                          : ""
                      }
                      onChange={(e) => set("appPassword", e.target.value)}
                    />
                    <span className="mt-1 block text-xs text-zinc-500">
                      {t("invoice_mailbox_app_password_help", {
                        defaultValue:
                          "Not the account password: create an app-specific password in the mail provider's security settings.",
                      })}
                    </span>
                  </label>

                  {form.provider === "gmail" && (
                    <>
                      <label className="block">
                        <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                          {t("invoice_mailbox_ingest_mode", { defaultValue: "Collect" })}
                        </span>
                        <select
                          className={inputCls}
                          value={form.ingestMode}
                          onChange={(e) => set("ingestMode", e.target.value)}
                        >
                          <option value="all">
                            {t("invoice_mailbox_ingest_all", { defaultValue: "Every incoming message" })}
                          </option>
                          <option value="label">
                            {t("invoice_mailbox_ingest_label", { defaultValue: "Only one label" })}
                          </option>
                        </select>
                      </label>
                      {form.ingestMode === "label" && (
                        <label className="block">
                          <span className="mb-1 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                            {t("invoice_mailbox_label_name", { defaultValue: "Label" })}
                          </span>
                          <input
                            className={inputCls}
                            value={form.labelName}
                            onChange={(e) => set("labelName", e.target.value)}
                          />
                        </label>
                      )}
                    </>
                  )}

                  <label className="flex items-center gap-2 md:col-span-2">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => set("isActive", e.target.checked)}
                    />
                    <span className="text-sm">
                      {t("invoice_mailbox_active", { defaultValue: "Collect mail from this address" })}
                    </span>
                  </label>

                  {/* The supervisor reconciles its IMAP runners once a minute,
                      so "nothing happened yet" is the expected first answer. */}
                  <p className="text-xs text-zinc-500 md:col-span-2">
                    {t("invoice_mailbox_start_delay", {
                      defaultValue:
                        "Collection starts within a minute of saving. Only mail that arrives after that is collected — nothing already in the mailbox is pulled in.",
                    })}
                  </p>
                </div>

                {fieldError && (
                  <div className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
                    {fieldError}
                  </div>
                )}

                <div className="mt-5 flex flex-wrap gap-2">
                  <button type="button" className={primaryBtnCls} disabled={busy} onClick={save}>
                    {t("save", { defaultValue: "Save" })}
                  </button>
                  <button type="button" className={btnCls} disabled={busy} onClick={test}>
                    {t("invoice_mailbox_test", { defaultValue: "Test connection" })}
                  </button>
                  {selected && selected.isActive && selected.hasCredentials && (
                    <button
                      type="button"
                      className={btnCls + " inline-flex items-center gap-1.5"}
                      disabled={busy}
                      onClick={checkMailNow}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      {t("invoice_mailbox_check_now", { defaultValue: "Check mail now" })}
                    </button>
                  )}
                  {selected && (
                    <div className="flex gap-2 md:ml-auto">
                      {stillConnected ? (
                        <button
                          type="button"
                          className={btnCls}
                          disabled={busy}
                          onClick={() => setPendingRemoval("disable")}
                        >
                          {t("invoice_mailbox_stop", { defaultValue: "Stop collecting" })}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={dangerBtnCls}
                          disabled={busy}
                          onClick={() => setPendingRemoval("delete")}
                        >
                          {t("delete", { defaultValue: "Delete" })}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {pendingRemoval && (
                  <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
                    <p>
                      {pendingRemoval === "disable"
                        ? t("invoice_mailbox_stop_confirm", {
                            defaultValue:
                              "The saved password is erased and no new mail is collected. Everything already received stays in the register.",
                          })
                        : t("invoice_mailbox_delete_confirm", {
                            defaultValue: "The mailbox is removed for good. Only possible while it has received nothing.",
                          })}
                    </p>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        className={pendingRemoval === "delete" ? dangerBtnCls : primaryBtnCls}
                        disabled={busy}
                        onClick={() => remove(pendingRemoval)}
                      >
                        {t("confirm", { defaultValue: "Confirm" })}
                      </button>
                      <button type="button" className={btnCls} disabled={busy} onClick={() => setPendingRemoval(null)}>
                        {t("cancel", { defaultValue: "Cancel" })}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {selected && <MailboxHealth mailbox={selected} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * "Is it working?" — the three answers that matter, in the order they cost
 * money: mail that arrived and became nothing, a mailbox that has gone quiet,
 * and the connection itself.
 */
function MailboxHealth({ mailbox }: { mailbox: InvoiceMailbox }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-zinc-200/80 p-5 text-sm dark:border-zinc-800">
      <Subheading className="!mb-3 !font-semibold">
        {t("invoice_mailbox_health", { defaultValue: "State" })}
      </Subheading>

      {mailbox.alsoCollectedByDesk && (
        <div className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
          {t("invoice_mailbox_also_desk", {
            defaultValue:
              "This address is also collected into Desk — every invoice will become a task card as well.",
          })}
        </div>
      )}

      {mailbox.lastError && (
        <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {mailbox.lastError}
        </div>
      )}

      {mailbox.ingestErrorCount > 0 && (
        <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-red-700 dark:bg-red-950/40 dark:text-red-300">
          <p className="font-medium">
            {t("invoice_mailbox_lost_explained", {
              defaultValue:
                "{{count}} message(s) arrived in the last {{days}} days and became no document. They are not in the register.",
              count: mailbox.ingestErrorCount,
              days: mailbox.ingestErrorWindowDays,
            })}
          </p>
          <ul className="mt-2 space-y-1 text-xs">
            {mailbox.recentErrors.map((row) => (
              <li key={row.id}>
                {row.at ? formatPrettyDate(row.at, "lt-LT", true) + " — " : ""}
                {row.text}
              </li>
            ))}
          </ul>
        </div>
      )}

      {mailbox.silent && (
        <div className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
          {t("invoice_mailbox_silent_explained", {
            defaultValue:
              "Nothing has arrived for {{count}} day(s), which is unusual for this mailbox. A forwarding rule or an MX record may be broken upstream.",
            count: mailbox.quietDays,
          })}
        </div>
      )}

      <dl className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <dt className="text-zinc-500">{t("invoice_mailbox_last_sync", { defaultValue: "Last checked" })}</dt>
          <dd>{mailbox.lastSyncedAt ? formatPrettyDate(mailbox.lastSyncedAt, "lt-LT", true) : "—"}</dd>
        </div>
        <div>
          <dt className="text-zinc-500">{t("invoice_mailbox_last_received", { defaultValue: "Last message" })}</dt>
          <dd>{mailbox.lastReceivedAt ? formatPrettyDate(mailbox.lastReceivedAt, "lt-LT", true) : "—"}</dd>
        </div>
        <div>
          <dt className="text-zinc-500">
            {t("invoice_mailbox_traffic", { defaultValue: "Messages in {{days}} days", days: mailbox.messageWindowDays })}
          </dt>
          <dd>{mailbox.messageCount}</dd>
        </div>
        <div>
          <dt className="text-zinc-500">{t("invoice_mailbox_imap_host", { defaultValue: "IMAP server" })}</dt>
          <dd>
            {mailbox.imapHost}:{mailbox.imapPort}
          </dd>
        </div>
      </dl>
    </div>
  );
}
